/**
 * Русский словарь.
 *
 * Термины — как в документации gdsgate: ресурс, каталог, политика, метка,
 * журнал аудита, запись сессии, поставщик удостоверений, короткоживущий
 * сертификат. Фразы короткие и утвердительные; там, где речь о последствиях
 * действия, формулировки взяты из admin-guide.
 *
 * Два разных «поставщика» рядом, поэтому они разведены словами: «поставщик» —
 * это мы, продающие услугу; «поставщик удостоверений» — то, что выдаёт токены,
 * и он бывает наш или свой.
 *
 * Тип `Messages` выведен из `en`, поэтому пропущенный или лишний ключ — ошибка
 * сборки, а не находка на экране.
 */

import type { Messages } from './types';

export const ru: Messages = {
  // ---------- общее ----------
  'common.brand': 'gdsgate',
  'common.console': 'консоль',
  'common.cancel': 'Отмена',
  'common.close': 'Закрыть',
  'common.retry': 'Перечитать',
  'common.signOut': 'Выйти',
  'common.loading': 'Загрузка',
  'common.actions': 'Действия',
  'common.dash': '—',
  'common.copy': 'Скопировать',
  'common.copied': 'Скопировано.',
  'time.ago': '{value} назад',
  'common.why': 'Почему так',
  'common.unread': 'Не прочитано',
  'table.expand': 'Подробно',
  'table.collapse': 'Свернуть',
  'common.copyByHand':
    'Браузер не дал странице скопировать это. Выделите строку и скопируйте вручную.',

  // ---------- оболочка ----------
  'nav.skipToContent': 'К содержимому',
  'nav.primary': 'Основная навигация',
  'nav.group.fleet': 'Парк',
  'nav.group.tenancy': 'Аренда',
  'nav.group.account': 'Учётная запись',
  'nav.account.tenants': 'Арендаторы',
  'nav.account.tenancy': 'Моя аренда',
  'nav.account.usage': 'Потребление',
  'nav.account.rollout': 'Версии',
  'nav.account.identityProvider': 'Поставщик удостоверений',
  'nav.account.password': 'Пароль',
  'nav.cluster.group': 'Кластер',
  'nav.cluster.catalog': 'Каталог',
  'shell.role.providerAdmin': 'Оператор поставщика',
  'shell.role.providerSupport': 'Поддержка, только чтение',
  'shell.role.tenantAdmin': 'Представитель арендатора',
  'shell.role.cluster': 'Администратор кластера',
  'shell.scope.provider': 'Все арендаторы',
  'shell.scope.provider.sub': 'поставщик',
  'shell.breadcrumb': 'Хлебные крошки',

  'topbar.theme.toLight': 'Переключить на светлую тему',
  'topbar.theme.toDark': 'Переключить на тёмную тему',
  'topbar.language': 'Язык',
  'topbar.language.en': 'English',
  'topbar.language.ru': 'Русский',

  // ---------- вход в аккаунт ----------
  'login.heading': 'Вход в аккаунт',
  'login.subtitle':
    'Аккаунт у поставщика: арендаторы, их состояние и то, каким способом ваши люди входят в кластер.',
  'login.email.label': 'Почта',
  'login.email.placeholder': 'you@example.com',
  'login.password.label': 'Пароль',
  'login.submit': 'Войти',
  'login.submitting': 'Входим',
  'login.failed': 'Войти не удалось',
  'login.badCredentials':
    'Адрес и пароль не подходят друг к другу. Что именно неверно, сознательно не сказано: любая разница здесь — способ узнать, какие адреса заведены.',
  'login.noReset':
    'Сброса по почте нет: продукт не отправляет почту. Пароль задаётся из командной строки тем, у кого уже есть доступ к узлу.',
  'login.check.emailRequired': 'Введите адрес почты.',
  'login.check.emailInvalid': 'Это не адрес почты.',
  'login.check.passwordRequired': 'Введите пароль.',
  'login.tooMany.title': 'Слишком много попыток',
  'login.tooMany.body':
    'Сервер считает неудачные попытки по этому адресу в окне, и окно переполнено. Оно опустеет само.',
  'login.tooMany.wait': 'Повторите через {wait}.',
  'login.tooMany.unknownWait':
    'Сколько осталось ждать, в ответе не сказано, и консоль не станет это выдумывать. Речь о минутах, не о часах.',
  'login.tooMany.rightPassword':
    'Верный пароль в переполненном окне тоже не проходит. Счётчик, пропускающий правильную догадку, — не счётчик.',
  'login.quote':
    'Доступ к каждому узлу, базе и кластеру: с личностью, под политикой, с записью — через один шлюз.',
  'login.quote.by': 'один бинарь · проверено политикой · записано',
  'login.terminal.comment': '# после одного входа привычные инструменты просто работают',
  'login.terminal.login': 'gdsgate login',
  'login.terminal.ssh': 'ssh app-prod-03',
  'login.terminal.ssh.note': '→ через gdsgate',
  'login.terminal.psql': 'psql -h orders-db',
  'login.terminal.psql.note': '→ проверено политикой',
  'login.terminal.kubectl': 'kubectl get pods',
  'login.terminal.kubectl.note': '→ записано',

  // ---------- свой пароль ----------
  'password.title': 'Пароль',
  'password.section': 'Сменить пароль',
  'password.forAccount': 'Для {email}.',
  'password.current.label': 'Текущий пароль',
  'password.current.hint':
    'Спрашивается, хотя вы уже вошли: украденной сессии не должно хватать, чтобы забрать учётную запись.',
  'password.next.label': 'Новый пароль',
  'password.next.hint':
    'Не короче {least} символов. Длина — единственное требование, которое здесь что-то покупает.',
  'password.repeat.label': 'Новый пароль ещё раз',
  'password.submit': 'Сменить пароль',
  'password.submitting': 'Меняем',
  'password.check.currentRequired': 'Введите текущий пароль.',
  'password.check.currentWrong': 'Это не текущий пароль.',
  'password.check.tooShort': 'Не короче {least} символов.',
  'password.check.sameAsCurrent': 'Это пароль, который уже используется.',
  'password.check.mismatch': 'Введённое не совпадает.',
  'password.check.refused': 'Сервер не принял такой пароль.',
  'password.effect.title': 'Что делает смена',
  'password.effect.sessions':
    'Закрываются все сессии этой учётной записи, включая текущую: сессия, пережившая смену пароля, — ровно то, что смена должна была закрыть.',
  'password.effect.noReset':
    'Пути назад через почту нет. Забытый новый пароль означает просьбу к тому, у кого есть доступ к узлу, задать другой.',

  // ---------- вход в консоль кластера ----------
  'clusterLogin.heading': 'Вход в консоль кластера',
  'clusterLogin.subtitle':
    'Эта консоль принадлежит {domain}. Она пускает людей через поставщика удостоверений, выбранного этим арендатором, — учётная запись у поставщика здесь не открывает ничего.',
  'clusterLogin.provider.title': 'Этот кластер пускает людей через',
  'clusterLogin.provider.ours': 'поставщика удостоверений поставщика',
  'clusterLogin.provider.byProvider':
    'Поставщик удостоверений, который держит поставщик. Ваших людей заводит и отзывает он.',
  'clusterLogin.provider.byTenancy':
    'Собственный поставщик удостоверений этого арендатора. Своих людей вы заводите и отзываете сами.',
  'clusterLogin.continue': 'Перейти к своему поставщику удостоверений',
  'clusterLogin.continuing': 'Начинаем',
  'clusterLogin.continue.hint':
    'Вас передадут провайдеру и вернут сюда. То, с чем сверяют возврат, лежит на сервере под ключом, который есть только у этого браузера.',
  'clusterLogin.since': 'В этом состоянии с {when}.',
  'clusterLogin.reconfiguring.title': 'Вход перенастраивается',
  'clusterLogin.reconfiguring.body':
    'Кластер переходит на другого поставщика удостоверений. Новых сессий не выдаётся, пока это не кончится; уже открытые не рвутся.',
  'clusterLogin.notConfigured.title': 'Поставщик удостоверений не применён',
  'clusterLogin.notConfigured.body':
    'Кластер поднят, и войти в него не может никто, пока выбор арендатора не применён.',
  'clusterLogin.refused.title': 'Вход прошёл, а этот кластер вас не пускает',
  'clusterLogin.refused.body':
    'Ваш поставщик удостоверений за вас поручился, а политика этого арендатора не называет вас администратором кластера. Сессия не открыта вовсе.',
  'clusterLogin.refused.group':
    'Имя группы, которую ищет этот кластер, зафиксировано при заведении арендатора и не меняется. В группу вас добавляет тот, кто держит вашего поставщика удостоверений.',
  'clusterLogin.badTarget.title': 'В ответе не назван адрес, куда идти',
  'clusterLogin.badTarget.body':
    'Начало входа вернуло не адрес провайдера, и консоль по нему не пошла. Ничего не произошло.',
  'clusterLogin.account.title': 'Выбор делается в аккаунте',
  'clusterLogin.account.body':
    'Он на домене поставщика и открывается учётной записью у поставщика — чтобы кластер, в который никто не может войти, всё равно можно было починить.',
  'clusterLogin.account.open': 'Открыть аккаунт',

  // ---------- тупики ----------
  'unknownRole.title': 'Консоль не знает такой роли',
  'unknownRole.body':
    'У учётной записи роль, под которую эта консоль не сделана. Догадаться до ближайшей известной значило бы показать кнопки, которые сервер отклонит.',
  'unknownRole.account': 'Вошли как {email}.',
  'clusterNoAccess.title': 'Вошли, а управлять нечем',
  'clusterNoAccess.body':
    'Поставщик удостоверений за вас поручился, а ни в одной группе, которой этот кластер даёт права, вас нет. В нужную группу вас добавляет тот, кто держит этого поставщика.',
  'didNotStart.title': 'Консоль не запустилась',
  'didNotStart.body':
    'Какую консоль обслуживает этот адрес, решает контур, и он не ответил. Сама консоль решить это не может: догадка показала бы одному арендатору консоль другого.',
  'didNotStart.detail': 'Что вернулось браузеру: {detail}',
  'didNotStart.retry': 'Попробовать снова',

  // ---------- арендаторы ----------
  'tenants.title': 'Арендаторы',
  'tenants.create': 'Завести арендатора',
  'tenants.search.label': 'Поиск по арендаторам',
  'tenants.search.placeholder': 'Имя, слаг, домен',
  'tenants.filter.label': 'Фильтр по состоянию',
  'tenants.filter.all': 'Все',
  'tenants.col.name': 'Арендатор',
  'tenants.col.domain': 'Домен',
  'tenants.col.state': 'Состояние',
  'tenants.col.declared': 'Объявленное',
  'tenants.col.version': 'Версия',
  'tenants.col.created': 'Заведён',
  'tenants.declared.told': 'Сказано',
  'tenants.declared.never': 'ни разу',
  'tenants.empty.title': 'Ничего не подходит',
  'tenants.empty.body': 'Измените фильтр или строку поиска.',
  'tenants.none.title': 'Здесь нет арендаторов',
  'tenants.none.body':
    'Учётная запись видит тех арендаторов, на которых имеет право. Пустой список — ответ, а не сбой.',
  'tenants.summary.total': 'Арендаторов',
  'tenants.summary.ready': 'Сдано',
  'tenants.summary.behind': 'Не сказано кластеру',
  'tenants.summary.failed': 'Не принят',

  // ---------- жизненный цикл арендатора ----------
  'tenantState.requested': 'Объявлен',
  'tenantState.requested.hint': 'Объявлен, и к нему ещё ничего не применяли.',
  'tenantState.provisioning': 'Разворачивается',
  'tenantState.provisioning.hint':
    'Желаемое состояние применяется. Арендатор не сдан, пока диагностика не пройдёт.',
  'tenantState.ready': 'Сдан',
  'tenantState.ready.hint':
    'Последнее применение прошло все шаги, и диагностика вышла нулём на каждой роли.',
  'tenantState.failed': 'Не принят',
  'tenantState.failed.hint':
    'Последнее применение не завершилось либо диагностика вернула ненулевой код. Кластер может и работать; принятым он не считается.',
  'tenantState.suspended': 'Приостановлен',
  'tenantState.suspended.hint':
    'Остановлен сознательно. Данные на месте, а применение желаемого состояния отклоняется: оно вернуло бы то, что убрали намеренно.',
  'tenantState.tearingDown': 'Разбирается',
  'tenantState.tearingDown.hint': 'Разбирается в порядке контракта провижининга.',
  'tenantState.removed': 'Разобран',
  'tenantState.removed.hint':
    'Разобран. Запись остаётся, чтобы слаг и домен не достались по ошибке кому-то другому.',
  'tenantState.unknown': 'Неизвестное состояние',
  'tenantState.unknown.hint': 'Управляющий контур сообщает состояние, которого консоль не знает.',

  // ---------- карточка арендатора ----------
  'tenant.back': 'Все арендаторы',
  'tenant.section.fingerprint': 'Первый контакт',
  'tenant.section.idp': 'Поставщик удостоверений',
  'tenant.field.slug': 'Слаг',
  'tenant.field.consoleDomain': 'Домен консоли',
  'tenant.field.gatewayDomain': 'Шлюз',
  'tenant.field.adminGroup': 'Группа администраторов',
  'tenant.field.state': 'Состояние',
  'tenant.field.version': 'Версия gdsgate',
  'tenant.field.created': 'Заведён',
  'tenant.field.updated': 'Изменён',
  'tenant.adminGroup.note':
    'Зафиксирована при заведении. Имя подставлено в стартовую политику кластера, и переписать его управляющий контур не может — поэтому при смене поставщика удостоверений группа не двигается.',
  'tenant.fingerprint.none':
    'Отпечатка пока нет: он появляется, когда шлюз впервые поднялся. Это первое, что нужно клиенту этого арендатора.',
  'tenant.fingerprint.note':
    'Единственное в первом контакте клиента, что приходит не от того, кто ответил. Передаётся в gdsgate connect рядом с доменом.',
  'tenant.declared.behind': 'Часть объявленного кластеру не сказана',
  'tenant.idp.absent': 'В ответе нет блока поставщика удостоверений для этого арендатора.',
  'tenant.idp.applying':
    'Идёт смена поставщика удостоверений. Пока она не кончится, люди этого арендатора не получат новой сессии.',
  'tenant.idp.failed':
    'Кластер остался на том, что выполнял. Арендатор исправляет это из своего аккаунта.',
  'tenant.idp.note':
    'Показано, но не задаётся. Маршрута, меняющего поставщика удостоверений по слагу, нет ни у кого: тот, кто мог бы указать чужой кластер на провайдера, мог бы указать его на такого, чьи токены выпускает сам.',
  'tenant.catalog.note':
    'Показано, но не правится. Каталог правят в консоли самого кластера — чтобы изменение донесло имя человека до аудита арендатора.',
  'tenant.notFound.title': 'Такого арендатора нет',
  'tenant.notFound.body':
    'Арендатора с таким именем нет, либо эта учётная запись не имеет на него прав. Ответ намеренно один и тот же.',
  'tenant.section.overview': 'Обзор',
  'tenant.sections.legend': 'Раздел арендатора',
  'tenant.tile.generation': 'Объявлено / применено',

  // ---------- своя аренда ----------
  'tenancy.title': 'Моя аренда',
  'tenancy.tile.resources': 'Ресурсов в каталоге',
  'tenancy.certificate.until': 'Сертификат до {when}',
  'tenancy.addresses.title': 'Адреса',
  'tenancy.version.title': 'Версия',
  'tenancy.version.untold': 'На доске нет строки этой аренды.',

  // ---------- потребление ----------
  'usage.title': 'Потребление',
  'usage.back': 'Назад к арендатору',
  'usage.period.from.short': 'С',
  'usage.period.to.short': 'По',
  'usage.period.hint':
    'Оба конца обязательны, и ни один не подставлен за вас. Окно полуоткрытое: событие ровно на его конце принадлежит следующему периоду.',
  'usage.period.utc': 'Каждый день читается как его полночь по UTC.',
  'usage.period.lastMonth': 'Прошлый месяц',
  'usage.period.thisMonth': 'Текущий месяц',
  'usage.submit': 'Прочитать цифры',
  'usage.submitting': 'Читаю',
  'usage.again': 'Прочитать заново',
  'usage.notPolled':
    'Здесь ничего не обновляется само. Две цифры из четырёх — вопросы к кластеру самого арендатора, и сам вопрос кластер записывает под именем спросившего: экран на таймере набил бы аудит клиента собственным любопытством консоли.',
  'usage.check.fromRequired': 'Выберите день, с которого начинается период.',
  'usage.check.toRequired': 'Выберите день, которым период кончается.',
  'usage.check.fromUnreadable': 'Это не дата.',
  'usage.check.toUnreadable': 'Это не дата.',
  'usage.check.order': 'Конец должен быть позже начала.',

  'usage.figure.connectors': 'Коннекторов заведено',
  'usage.figure.resources': 'Ресурсов обслуживается',
  'usage.figure.sessions': 'Сессий открыто за период',
  'usage.figure.people': 'Людей входило за период',

  'usage.asOf': 'На момент {when}. Это не цифра за период.',
  'usage.asOf.why':
    'Коннекторы и ресурсы посчитаны на момент чтения: у реестра узлов и каталога ресурсов gdsgate истории нет, это текущее состояние. За окно считаются только сессии и люди.',

  'usage.missing.title': 'Не посчитано',
  'usage.missing.body':
    'Отсутствующая цифра — это не ноль: по нулю можно выставить счёт. У каждой такой цифры вместо числа показана причина, названная сервером.',
  'usage.missing.noReason':
    'В ответе цифры нет, и причина не названа. Дырку числом здесь никто не закрывает.',
  'usage.missing.normal':
    'Это говорит, что источник не прочитался, а не что ничего не происходило. Сессии и люди считаются из выгрузки аудит-цепочки арендатора, и там, где управляющий контур до неё не дотягивается, они приходят отсутствующими.',
  'usage.gap.unknown': 'Не посчитана и цифра, которой эта консоль не рисует: {figure}.',

  'usage.connectors.section': 'Коннекторы',
  'usage.connectors.revoked': 'Отозванных личностей',
  'usage.connectors.revoked.hint':
    'В счёт не идёт. Стоит здесь потому, что «мы его отозвали» — это ответ на вопрос «почему цифра рядом упала».',
  'usage.connectors.notOnline':
    'Это не «коннекторы на связи»: сигнала «туннель поднят» у gdsgate нет, поэтому считаются строки реестра. Шлюз арендатора и личность управляющего контура — тоже строки реестра, и ни одна из них не в счёте.',
  'usage.connectors.col.id': 'Идентификатор в реестре',
  'usage.connectors.col.claimed': 'Имя, которым узел назвал себя',
  'usage.connectors.claimed.mark': 'заявлено узлом, не проверено',
  'usage.connectors.col.enrolled': 'Зарегистрирован',
  'usage.connectors.claimed.hint':
    'Провижн-токен выдаётся на роль, а не на машину, поэтому проверять в этом имени кластеру нечего. Поэтому пометка стоит рядом с именем в каждой строке, а не в сноске.',
  'usage.connectors.claimed.none': 'Узел никак себя не назвал.',
  'usage.connectors.enrolled.unknown': 'Реестр напечатал отметку, которая не является моментом времени.',
  'usage.connectors.none': 'Живых личностей коннекторов у этого арендатора нет.',

  'usage.resources.note':
    'Каталог кластера, а не объявленный здесь. Пока изменение не применено, они расходятся, а счёт по объявленному брал бы деньги за то, до чего пока не дотянуться.',
  'usage.resources.byKind': 'По видам',

  'usage.sessions.count.hint':
    'Сессия принадлежит окну, в котором открылась, и только ему — целиком со своей длительностью.',
  'usage.sessions.closed': 'Из них закрылось',
  'usage.sessions.unclosed': 'Ещё идут либо закрытие не доехало',
  'usage.sessions.unclosed.hint':
    'Посчитаны выше — строка открытия доказывает, что сессия была, — и в длительность ниже не входят ничем. «Сейчас минус открытие» выставило бы счёт за сессию, которая могла кончиться месяц назад.',
  'usage.sessions.duration': 'Длительность закрывшихся сессий',
  'usage.sessions.duration.parts': '{hours} ч {minutes} мин {seconds} с',
  'usage.sessions.recorded': 'Записано записи сессий',
  'usage.sessions.recorded.hint':
    'Байты записи, а не переданные байты. Сессия к базе, в Kubernetes, к MCP-серверу или по обычному TCP закрывается нулём, сколько бы трафика через неё ни прошло. Объём релея в цепочке не измеряет ничто.',
  'usage.sessions.orphan': 'Закрытий без открытия',
  'usage.sessions.orphan.hint':
    'Ни в одну другую цифру не входят: без открытия нет ни начала, по которому датировать сессию, ни длительности. Опубликовано, чтобы недосчёт был виден.',
  'usage.sessions.byKind': 'Открыто, по протоколам',

  'usage.people.count.hint':
    'Различные субъекты. Один человек — одна единица, сколько бы раз он ни входил.',
  'usage.people.rows': 'На этих людей пришлось строк входа: {rows}.',
  'usage.people.rows.short': 'строк входа: {rows}',
  'usage.people.rows.hint':
    'Строки — не люди, и никакая арифметика их в людей не превращает. gdsgate пишет не больше строки на человека в сутки на процесс Auth, поэтому перезапуск или вторая реплика добавляют строк и не добавляют людей.',

  'usage.howCounted': 'Как это посчитано',
  'usage.bytes': 'байт',
  'usage.gaps.title': 'Чего в этих цифрах нет',
  'usage.gaps.relay':
    'Объёма релея. В аудит-цепочке арендатора его не измеряет ничто, поэтому ни одна цифра здесь не про трафик.',
  'usage.gaps.node':
    'Узла, который завёлся и был отозван внутри периода. Реестр держит по строке на личность и без истории, поэтому когда он был жив, не сказано нигде.',
  'usage.gaps.resource':
    'Ресурса, объявленного и снятого внутри периода. Каталог — множество, а не журнал.',
  'usage.gaps.online':
    'Коннекторов на связи. Сигнала «туннель коннектора поднят» gdsgate не публикует, поэтому дальше слова «заведён» цифра коннекторов не идёт.',
  'usage.gaps.unclosed':
    'Длительности незакрывшейся сессии и закрытия, чьё открытие не было записано. И то и другое показано отдельными счётчиками выше, а не подмешано в итоги.',

  'usage.notNameable.title': 'Ваш провайдер называет вас так, как кластер не запишет',
  'usage.notNameable.body':
    'Не прочитано ничего. Две из этих цифр спрашиваются у кластера самого арендатора, а он записывает вопрос под именем спросившего; gdsgate принимает имя из букв, цифр, пробелов и . _ - @ + ’ и ничего больше. Чинится у того поставщика удостоверений, который вас так называет.',

  // ---------- версии и выкатка ----------
  'rollout.title': 'Версии и выкатка',

  'rollout.none.title': 'Выкатка не идёт',
  'rollout.start': 'Начать выкатку',
  'rollout.stop': 'Остановить выкатку',
  'rollout.field.actor': 'Начал',
  'rollout.field.finished': 'Окончена',
  'rollout.field.finished.untold':
    'Ответ не сказал когда. Другой момент вместо этого здесь не подставляется.',
  'rollout.field.changed': 'Состояние сменилось',

  'rollout.tally.moved': 'Сдвинуто',
  'rollout.tally.failed': 'Не сдвинулось',
  'rollout.tally.pending': 'Ещё не дошли',
  'rollout.tally.crosses': 'Названных разрывов',
  'rollout.tally.notWorkedOut': 'Не посчитано',
  'rollout.tally.tenants': 'Арендаторов на доске',
  'rollout.tally.declared': 'Объявленных версий',
  'rollout.tally.broken': 'С названным разрывом',
  'rollout.tally.notMovable': 'Ни в одной волне',

  'rollout.filter.wave': 'Отбор по волне',
  'rollout.filter.wave.all': 'Все',
  'rollout.filter.step': 'Отбор по сдвигу',
  'rollout.filter.step.all': 'Все',
  'rollout.search.label': 'Поиск по доске',
  'rollout.search.placeholder': 'Слаг',

  'rollout.col.tenant': 'Арендатор',
  'rollout.col.state': 'Состояние',
  'rollout.col.wave': 'Волна',
  'rollout.col.declared': 'Объявлено',
  'rollout.col.authority': 'authority',
  'rollout.col.proxy': 'proxy',
  'rollout.col.pairs': 'Пары',
  'rollout.col.observed': 'Прочитано',
  'rollout.col.step': 'Сдвиг',
  'rollout.observed.never': 'Никогда',
  'rollout.tenant.open': 'Открыть карточку',
  'rollout.wave.refused': 'Волна не изменена.',

  'rollout.state.applying': 'Двигается',
  'rollout.state.waiting': 'Ждёт подписи',
  'rollout.state.halted': 'Остановлена',
  'rollout.state.done': 'Кончилась',
  'rollout.state.unknown': 'Состояние, которого консоль не знает',

  'rollout.advance.blocked.title': 'Подписать сейчас нельзя',
  'rollout.advance.blocked.noReason':
    'В ответе сказано, что подписать нельзя, и не названо почему. Догадываться консоль не станет.',
  'rollout.advance.submit': 'Подписать волну',

  'rollout.retry.blocked.title': 'Повторить сдвиг сейчас нельзя',
  'rollout.retry.blocked.noReason':
    'Ответ сказал, что повторить нельзя, и причины не назвал. Догадываться о ней здесь никто не станет.',
  'rollout.retry.submit': 'Повторить сдвиг',

  'rollout.forecast.title': 'Что пересекает этот переход',
  'rollout.forecast.body':
    'Разрывы, которые gdsgate назвал сам, лежащие между тем, на чём парк видели, и той версией, на которую его двигают.',
  'rollout.forecast.none':
    'Между тем, на чём парк видели, и этой версией не названо ничего. Это отсутствие названного разрыва, а не обещание, что переход безопасен.',
  'rollout.namedIn': 'Названо в:',

  'rollout.wave.canary': 'Канареечная',
  'rollout.wave.rest': 'Остальные',
  'rollout.wave.unknown': 'Волна, которой консоль не знает',
  'rollout.wave.choose': 'Волна',
  'rollout.wave.put': 'Назначить в эту волну',
  'rollout.wave.putting': 'Назначаю',
  'rollout.wave.underWay':
    'Пока идёт выкатка — нельзя: остановите её, переложите арендатора и начните новую.',

  'rollout.unmeasurable.title': 'Чего узнать нечем вовсе',
  'rollout.unmeasurable.body':
    'Две составные части из четырёх о своей версии не сообщают ничего и при нынешнем устройстве не сообщат. Это не показание, которое не удалось снять, — спрашивать не у чего.',
  'rollout.unmeasurable.pairs': 'Поэтому эти пары не измеряются ни у кого:',
  'rollout.unmeasurable.consequence':
    'Коннектор живёт в сети самого клиента и обновляется клиентом, то есть расходится дальше всех четырёх, — и его пару здесь как раз нечем сравнить.',

  'rollout.component.notRead': 'Не прочитано',
  'rollout.component.unreadable': 'Прочитать нечем',
  'rollout.component.noReason':
    'В ответе версии нет, и причина не названа. Подставлять сюда своё консоль не станет.',

  'rollout.verdict.same': 'Одна сборка',
  'rollout.verdict.same.meaning': 'Оба конца видели на одной сборке.',
  'rollout.verdict.noNamedBreak': 'Ничего не названо',
  'rollout.verdict.noNamedBreak.meaning':
    'Версии разные, и между ними не названо ни одного разрыва, задевающего эту пару. Это не обещание, что они работают вместе: gdsgate обещает совместимость в пределах одного тега продукта.',
  'rollout.verdict.broken': 'Названный разрыв',
  'rollout.verdict.broken.meaning':
    'Между теми версиями, на которых эти две части видели, лежит разрыв, названный самим gdsgate.',
  'rollout.verdict.untold': 'Неизвестно',
  'rollout.verdict.untold.meaning':
    'Один конец или оба прочитать не удалось, поэтому сравнивать нечего. Причина называет, какой именно конец.',
  'rollout.verdict.unknown': 'Вердикт, которого консоль не знает',
  'rollout.verdict.unknown.meaning':
    'В ответе пришёл вердикт, для которого у консоли нет формулировки. Он показан, а не выброшен.',
  'rollout.broken.title': 'Названный разрыв внутри этого арендатора',
  'rollout.broken.body':
    'Две его собственные составные части видели на версиях, между которыми gdsgate назвал разрыв. Это не даёт подписать волну, в которой стоит этот арендатор.',

  'rollout.tenant.declared.note':
    'То, что кто-то набрал: он говорит, что подтянет следующий старт контейнера, а не что выполняется сейчас. Всё, что выше, — это то, что ответили сами части.',
  'rollout.tenant.observed.never':
    'Ни разу. Эти части не читали с тех пор, как управляющий контур начал их записывать.',
  'rollout.tenant.observed.how':
    'Показания снимаются во время применения желаемого состояния, и вызова, который только перечитал бы их, нет.',
  'rollout.tenant.notMovable':
    'Этого арендатора не двигает ни одна волна: двинуть его значило бы применить желаемое состояние, а это вернуло бы то, что забрали намеренно.',

  'rollout.move.title': 'Что эта выкатка с ним сделала',
  'rollout.move.from': 'С версии',
  'rollout.move.pending': 'Ещё не двигали',
  'rollout.move.moved': 'Сдвинут',
  'rollout.move.movedAfterFailure': 'Сдвинут после провала',
  'rollout.move.failed': 'Не сдвинут',
  'rollout.move.unknown': 'Исход, которого консоль не знает',
  'rollout.move.attempts': {
    one: '{count} попытка',
    few: '{count} попытки',
    many: '{count} попыток',
    other: '{count} попытки',
  },
  'rollout.move.failures': {
    one: 'из них {count} неудачная',
    few: 'из них {count} неудачные',
    many: 'из них {count} неудачных',
    other: 'из них {count} неудачной',
  },

  'rollout.tenants.none.title': 'Показывать нечего',
  'rollout.tenants.none.body': 'У этой учётной записи нет прав ни на одного, либо не заведено ни одного.',

  'rolloutStart.title': 'Начать выкатку',
  'rolloutStart.subtitle': 'Одна версия, волна за волной, по одному арендатору за раз.',
  'rolloutStart.target.label': 'Версия, на которую двигаем парк',
  'rolloutStart.target.hint':
    'Тег образа, на который двигается каждая волна, ровно в том виде, в каком он будет записан в compose-файл каждого арендатора.',
  'rolloutStart.target.fromRegistry': 'Из реестра образов: версии, которые в нём есть, свежие сверху.',
  'rolloutStart.target.choose': 'Выберите версию…',
  'rolloutStart.target.floating':
    'Плавающий тег принимается и завтра называет другую сборку. Прогноза у доски тогда нет, и она это говорит.',
  'rolloutStart.check.required': 'Наберите версию, на которую двигать.',
  'rolloutStart.willMove.title': 'Сейчас двинется волна «{wave}»',
  'rolloutStart.willMove.order':
    'Эти арендаторы, по одному за раз, в таком порядке. Каждого сначала нацеливают на версию, потом применяют желаемое состояние, а это перезапускает его контейнеры.',
  'rolloutStart.willMove.none':
    'В этой волне никого, а выкатку с пустой канареечной волной сервер отклоняет: парк двинулся бы целиком на основании волны, которая ничего не показала.',
  'rolloutStart.then.title': 'И дальше ничего, пока вы не скажете',
  'rolloutStart.then.body':
    'Когда с этой волной разберутся, она встанет и будет ждать. Следующие волны не начнутся, пока предыдущую не подпишут.',
  'rolloutStart.forecastLater':
    'Что пересекает переход, появится на доске сразу, как выкатка возникнет.',
  'rolloutStart.submit': 'Начать',
  'rolloutStart.submitting': 'Начинаю',
  'rolloutStart.refused.title': 'Выкатка не начата',
  'rolloutStart.refused.body':
    'Ничего не сдвинулось. Либо выкатка уже идёт, либо в первой волне нет ни одного арендатора, к которому можно применить желаемое состояние.',

  'rolloutSignOff.title': 'Подписать волну',
  'rolloutSignOff.subtitle': 'Волна «{wave}», версия {target}.',
  'rolloutSignOff.already.title': 'Что в этой волне уже произошло',
  'rolloutSignOff.already.moved': 'Сдвинуты и приняты:',
  'rolloutSignOff.already.repaired': 'Сдвинуты, но только после провала:',
  'rolloutSignOff.already.failed': 'Не сдвинуты, и сдвиг сказал почему:',
  'rolloutSignOff.already.pending': 'Ещё не разобрались:',
  'rolloutSignOff.already.nobody': 'Никого.',
  'rolloutSignOff.next.title': 'Что двинет подпись: волна «{wave}»',
  'rolloutSignOff.next.order':
    'Эти арендаторы поедут на {target} следующими, по одному за раз, в таком порядке — сразу после подписи.',
  'rolloutSignOff.next.none': 'В той волне никого, поэтому подпись ничего не двинет.',
  'rolloutSignOff.finishes.title': 'Это заканчивает выкатку',
  'rolloutSignOff.finishes.body':
    'Волны после этой нет, поэтому дальше ничего не поедет. Парк остаётся на том, на что его двинули.',
  'rolloutSignOff.judgement.title': 'Это ваше решение, а не машинное',
  'rolloutSignOff.judgement.body':
    'Управляющий контур отказывает в подписи, пока видит хоть одну находку. Пустой список снимает запрет и не говорит больше ничего: то, ради чего держат канарейку, — это звонок клиента, и увидеть его здесь нечем.',
  'rolloutSignOff.judgement.soak':
    'Ещё он настаивает, чтобы волна простояла на новой версии столько, сколько задано этому развёртыванию.',
  'rolloutSignOff.submit': 'Подписать и двинуть дальше',
  'rolloutSignOff.submitting': 'Подписываю',
  'rolloutSignOff.refused.title': 'Волна не подписана',
  'rolloutSignOff.refused.body':
    'Дальше ничего не сдвинулось. Когда причина в самой волне, фраза выше называет арендаторов, к которым надо пойти посмотреть.',

  'rolloutRetry.title': 'Повторить сдвиг',
  'rolloutRetry.subtitle': 'Волна «{wave}», версия {target}.',
  'rolloutRetry.willMove.title': 'Сдвинутся ещё раз',
  'rolloutRetry.willMove.body':
    'Арендаторы этой волны, которые не сдвинулись, — по одному за раз, в таком порядке. Каждого нацеливают на {target} и применяют желаемое состояние, ровно как на первой попытке.',
  'rolloutRetry.notAWayPast.title': 'Это не обход волны',
  'rolloutRetry.notAWayPast.body':
    'Трогают только тех, кто не сдвинулся. Волну всё равно надо будет подписать, а следующая всё равно ждёт этой подписи. Кого двигать, решает состояние шагов, а не выбор здесь.',
  'rolloutRetry.notAWayPast.record':
    'Починка не стирает того, что она понадобилась: у каждого арендатора остаётся счёт попыток и неудач.',
  'rolloutRetry.submit': 'Повторить',
  'rolloutRetry.submitting': 'Двигаю',
  'rolloutRetry.refused.title': 'Повтор не выполнен',
  'rolloutRetry.refused.body':
    'Ничего не сдвинулось. Когда причина в том, что все арендаторы волны сдвинулись, всё, что ещё держит волну, относится к самому арендатору, а не к сдвигу, и лечится на его карточке.',

  'rolloutStop.title': 'Остановить выкатку',
  'rolloutStop.subtitle': 'Версия {target}, волна «{wave}».',
  'rolloutStop.effect.title': 'Что остановка делает, а чего не делает',
  'rolloutStop.effect.moved':
    'Что уже сдвинулось — остаётся сдвинутым. Это не откат, и отката нет: вернуть парк назад — это другая выкатка, нацеленная на прежнюю версию.',
  'rolloutStop.effect.back':
    'Арендаторы, которых не двигали, остаются на прежнем, и дальше не трогают никого.',
  'rolloutStop.effect.next':
    'Начатый сдвиг доводится до конца: остановку слушают на следующем арендаторе, а не посреди текущего.',
  'rolloutStop.effect.ends':
    'Остановка заканчивает эту выкатку. Она остаётся на доске как последняя и больше ничего не запирает: следующую можно начинать сразу.',
  'rolloutStop.submit': 'Остановить',
  'rolloutStop.submitting': 'Останавливаю',

  // ---------- заведение арендатора ----------
  'createTenant.title': 'Завести арендатора',
  'createTenant.subtitle': 'Запись создаётся сейчас; кластер строится следующим применением.',
  'createTenant.name.label': 'Имя',
  'createTenant.name.placeholder': 'Acme Corp',
  'createTenant.slug.label': 'Слаг',
  'createTenant.slug.hint':
    'Одна DNS-метка: строчные буквы, цифры, дефис внутри. Становится профилем кластера и именем его каталога, и дальше не меняется.',
  'createTenant.slug.placeholder': 'acme',
  'createTenant.names.title': 'Имена выводятся из слага',
  'createTenant.names.body':
    'Консоль кластера отвечает на <слаг>-app, шлюз — на <слаг>-proxy, оба в зоне, где эта установка заводит аренды. Поля для них нет: зону выбирает не клиент, а на эти два имени и выпускается сертификат аренды.',
  'createTenant.version.label': 'Версия gdsgate',
  'createTenant.version.hint':
    'Тег образа, который выполняет этот арендатор. Задаётся на каждого отдельно — только так и возможен поэтапный выкат.',
  'createTenant.version.placeholder': 'v0.7.0',
  'createTenant.adminGroup.label': 'Группа администраторов',
  'createTenant.adminGroup.hint':
    'Значение в claim groups, которое делает человека администратором этого кластера. Потом не меняется. Имя, начинающееся с {namespace}, отклоняется: это пространство кластер держит за личностями, которые выпускает сам.',
  'createTenant.adminGroup.placeholder': 'acme-admins',
  'createTenant.adminGroup.slugTaken.title': 'С этим слагом обычное имя не подойдёт',
  'createTenant.adminGroup.slugTaken.body':
    'Имя, которое арендатор получил бы по образцу, — {name}, — попадает в пространство {namespace}, которое кластер держит за личностями, выпускаемыми им самим. Выберите имя, которое с {namespace} не начинается.',
  'createTenant.identity.title': 'Поставщик удостоверений здесь не выбирается',
  'createTenant.identity.body':
    'Кластер поднимается на поставщике удостоверений поставщика, чтобы быть доступным раньше, чем его владельца о чём-то спросили. Дальше арендатор выбирает сам.',
  'createTenant.provider.section': 'Поставщик удостоверений клиента',
  'createTenant.secrets.title': 'Секреты сюда не вводятся',
  'createTenant.secrets.body':
    'Учётные данные базы и ключи подписи кладёт провижионер, и через консоль они не проходят.',
  'createTenant.submit': 'Завести',
  'createTenant.submitting': 'Заводим',
  'createTenant.check.nameRequired': 'Введите имя.',
  'createTenant.check.slugRequired': 'Введите слаг.',
  'createTenant.check.slugFormat': 'Строчные буквы, цифры и дефисы.',
  'createTenant.check.versionRequired': 'Введите версию.',
  'createTenant.check.adminGroupRequired': 'Введите имя группы.',
  'createTenant.check.adminGroupReserved':
    'Пространство {namespace} кластер держит за личностями, которые выпускает сам, а провайдеру, выдавшему такую группу, отказывают в токене целиком. Выберите имя, которое с него не начинается.',
  'createTenant.check.adminGroupReservedTry':
    'Пространство {namespace} кластер держит за личностями, которые выпускает сам, а провайдеру, выдавшему такую группу, отказывают в токене целиком. Попробуйте {name}.',
  'createTenant.taken.title': 'Слаг или домен заняты',
  'createTenant.taken.body':
    'Одно из двух уже используется. Какое именно, в ответе не сказано — уникальность проверяет хранилище, — поэтому смотреть стоит на оба.',
  'createTenant.noProvider.title': 'В этом развёртывании нет своего поставщика удостоверений',
  'createTenant.noProvider.body':
    'Кластер нельзя поднять на том, чего нет, поэтому здесь придётся назвать поставщика клиента. Поля для него — ниже.',

  // ---------- поставщик удостоверений ----------
  'identityProvider.title': 'Поставщик удостоверений',
  // ---------- адрес возврата ----------
  'redirectUri.title': 'Зарегистрируйте этот адрес возврата у своего провайдера',
  'redirectUri.body':
    'Сюда ваш поставщик удостоверений возвращает браузер после входа. Зарегистрируйте адрес в клиенте, которым вы пользуетесь для этого кластера (в Keycloak это Valid redirect URIs); он сравнивается побайтно.',
  'redirectUri.label': 'Адрес возврата',
  'redirectUri.stable':
    'Он выводится из домена аренды и при смене поставщика удостоверений не меняется: у нового регистрируется та же строка.',
  'redirectUri.unnamed':
    'В ответе адреса возврата нет, и копировать нечего. Угаданный адрес — это адрес, на котором провайдер откажет.',

  'identityProvider.domains.note':
    'Консоль кластера — на домене аренды; шлюз, куда подключаются клиенты и коннекторы, — отдельное имя в том же домене.',
  'identityProvider.openConsole': 'Открыть консоль кластера',

  'identityProvider.inUse.title': 'Что выполняет кластер',
  'identityProvider.none':
    'Ничего не применено. Кластер поднят, и его консоль не пускает никого, пока это не изменится.',
  'identityProvider.declared.title': 'Что запрошено',
  'identityProvider.declared.note':
    'Объявлено и не выполняется. Кластер держит прежнее, пока смена не применена.',
  'identityProvider.adminGroup.note':
    'Какого бы поставщика вы ни выбрали, он обязан класть группу администраторов в claim groups, иначе люди войдут и получат отказ во всём.',

  'identityProvider.kind.provider': 'Поставщик удостоверений поставщика',
  'identityProvider.kind.provider.body':
    'Вход идёт через {issuer}, которого держим мы. Настраивать и обслуживать нечего.',
  'identityProvider.kind.own': 'Свой поставщик удостоверений',
  'identityProvider.kind.own.body':
    'Любой OIDC-провайдер, который у вас уже есть, — кластер проверяет токены по его опубликованным ключам подписи.',
  'identityProvider.provider.unavailable':
    'В этом развёртывании нет своего поставщика удостоверений, поэтому указать на него кластер нельзя. Назовите своего — ниже.',
  'identityProvider.provider.unavailable.title': 'Этот вариант здесь недоступен',
  'identityProvider.provider.unnamed':
    'Развёртывание сообщает, что держит своего поставщика удостоверений, но не говорит, где он, — описывать нечего.',
  'identityProvider.issuer.unnamed': 'В ответе адрес не назван.',

  'identityProvider.price.title': 'Чем вы за это платите',
  'identityProvider.price.provider':
    'Заведение и отзыв ваших людей переходят к поставщику: вы просите нас, и делаем это мы. Команде без своего каталога это меньше работы; команде со своим — отданный контроль над персоналом.',
  'identityProvider.price.own':
    'Ваши люди остаются вашими: отзыв у себя закрывает и консоль этого кластера, и доступ к его узлам и базам — одним действием. Обратная сторона: ошибка в этих полях закроет консоль для всех, включая вас, и путь назад — этот экран.',

  'identityProvider.change': 'Сменить провайдера',
  'identityProvider.running': 'Вход идёт через',
  'identityProvider.choose.legend': 'Кто выпускает токены, которым доверяет кластер',
  'identityProvider.own.section': 'Настройки провайдера',
  'identityProvider.field.kind': 'Вариант',
  'identityProvider.field.issuer': 'Издатель',
  'identityProvider.field.clientId': 'Идентификатор клиента',
  'identityProvider.field.audience': 'Аудитория',
  'identityProvider.field.signingAlgs': 'Алгоритмы подписи токенов',
  'identityProvider.issuer.hint':
    'Базовый адрес. По нему забирается документ обнаружения, и claim iss каждого токена обязан совпасть с ним побайтно. Без запроса, без фрагмента, без завершающей косой черты.',
  'identityProvider.issuer.placeholder': 'https://login.acme.example',
  'identityProvider.clientId.hint': 'OAuth-клиент, заведённый под gdsgate у вашего провайдера.',
  'identityProvider.clientId.placeholder': 'gdsgate',
  'identityProvider.audience.hint':
    'Ожидаемый claim aud. Оставьте пустым, чтобы использовать идентификатор клиента.',
  'identityProvider.audience.default': 'Идентификатор клиента',
  'identityProvider.signingAlgs.hint':
    'Ничего не отмечено — принимаются все асимметричные алгоритмы. Симметричные не принимаются никогда: ключ, которым такой токен проверяют, им же его и выпускают.',
  'identityProvider.signingAlgs.all': 'Все асимметричные',
  'identityProvider.secrets.title': 'Client secret сюда не вводится',
  'identityProvider.secrets.body':
    'gdsgate проверяет токены по вашим опубликованным ключам, а вход идёт против публичного клиента. Провайдер, выдающий токены только конфиденциальному клиенту, не подходит.',

  'identityProvider.effect.title': 'Что делает применение',
  'identityProvider.effect.restart':
    'Оно перенастраивает кластер. Auth читает настройки поставщика удостоверений один раз, при старте, поэтому применение выбора его перезапускает.',
  'identityProvider.effect.pause':
    'Пока это идёт, ни входов, ни новых доступов не выдаётся. Речь о секундах, не о минутах.',
  'identityProvider.effect.sessions': 'Уже открытые сессии продолжают работать.',
  'identityProvider.submit': 'Применить выбор',
  'identityProvider.startedAt': 'Начата {when}.',

  'identityProvider.check.issuerRequired': 'Введите адрес издателя.',
  'identityProvider.check.issuerScheme':
    'Издатель обязан начинаться с https://. Обнаружение и загрузка ключей по плоскому HTTP переписываются любым на пути.',
  'identityProvider.check.issuerNoHost': 'Здесь не назван хост.',
  'identityProvider.check.issuerQuery': 'Издатель — базовый адрес: без запроса и без фрагмента.',
  'identityProvider.check.issuerTrailingSlash':
    'Уберите завершающую косую черту. Адрес сравнивается побайтно с тем, что объявляет провайдер.',
  'identityProvider.check.issuerWhitespace': 'Здесь есть пробельные символы.',
  'identityProvider.check.clientIdRequired': 'Введите идентификатор клиента.',
  'identityProvider.check.clientIdWhitespace':
    'В идентификаторе клиента не бывает пробельных символов.',
  'identityProvider.check.audienceWhitespace':
    'В аудитории не бывает пробельных символов. Оставьте поле пустым, чтобы принять идентификатор клиента.',

  // ---------- состояние смены ----------
  'identityState.pending': 'Объявлено, не применено',
  'identityState.applying': 'Идёт применение',
  'identityState.applied': 'Применено',
  'identityState.rejected': 'Отклонено, ничего не тронуто',
  'identityState.failed': 'Применялось и не легло',
  'identityState.unknown': 'Состояние, которого консоль не знает',
  'identityState.pending.badge': 'Объявлено',
  'identityState.applying.badge': 'Идёт',
  'identityState.applied.badge': 'Применено',
  'identityState.rejected.badge': 'Отклонено',
  'identityState.failed.badge': 'Не легло',

  'identityState.unknown.badge': 'Неизвестно',

  'identityProvider.applying.body':
    'Провайдера спрашивают, и если он ответил — Auth перезапускают в него. Эта страница следит за этим сама.',
  'identityProvider.applying.sessions':
    'Пока это не кончится, входов не выдаётся. Уже открытые сессии не трогают.',
  'identityProvider.rejected.body':
    'Ничего не тронуто: файлы прежние, Auth не перезапускался. Исправьте настройки на стороне провайдера и примените снова.',
  'identityProvider.failed.body':
    'Проверка прошла, а применение не завершилось. Прежняя конфигурация записана обратно, поэтому никто из тех, кто мог войти, доступа не потерял.',
  'identityProvider.applyAgain': 'Применить тот же выбор снова',
  'identityProvider.dismiss': 'Убрать эту попытку',
  'identityProvider.revert': 'Вернуться к прежнему',
  'identityProvider.noPrevious':
    'Возвращаться некуда: к этому кластеру ни разу не применяли другого поставщика.',
  'identityProvider.underWay.title': 'Смена уже идёт',
  'identityProvider.underWay.body':
    'Для этого арендатора уже идёт одна смена поставщика удостоверений. Вторая отклоняется, а не ставится в очередь: каждая перезапускает управляющую плоскость арендатора.',
  'identityProvider.nothingToDismiss.title': 'Убирать нечего',
  'identityProvider.nothingToDismiss.body':
    'Последняя смена не падала, значит и отчёта нет. Состояние перечитано.',
  'identityProvider.nowhereBack.title': 'Возвращаться некуда',
  'identityProvider.nowhereBack.body':
    'К этому кластеру ни разу не применяли поставщика удостоверений, поэтому нет ничего, про что известно, что оно работало.',
  'identityProvider.refused.title': 'Выбор отклонён',
  'identityProvider.refused.body':
    'Он не прошёл проверки, которые делаются до того, как провайдера о чём-то спросят. Поля, к которым надо вернуться, названы ниже.',
  'identityProvider.notSole.title': 'Эта учётная запись не представляет одного арендатора',
  'identityProvider.notSole.body':
    'Выбор следует из учётной записи, а эта не привязана ни к одному арендатору либо привязана к нескольким.',
  'identityProvider.watch.gaveUp.title': 'Это идёт слишком долго',
  'identityProvider.watch.gaveUp.body':
    'Смена не завершилась дольше, чем можно держать замок этого арендатора. Оставшийся замок сервер снимает по времени; перечитать состояние безопасно.',

  // ---------- почему смена не легла ----------
  'identityFailure.issuerUnreachable':
    'Издатель не ответил: документ обнаружения забрать не удалось.',
  'identityFailure.issuerCertificate':
    'Провайдер ответил, а сертификат, который он предъявил, не прошёл проверку: истёк, подписан удостоверяющим центром, которому это развёртывание не доверяет, или выписан не на это имя. Он работает и отвечает всем остальным — разбираться надо с сертификатом. В строке ниже — дата, на которой он кончился.',
  'identityFailure.issuerMismatch':
    'Провайдер объявляет не того издателя, который введён. Они обязаны совпадать побайтно — чаще всего вместо издателя вставляют адрес консоли.',
  'identityFailure.algorithmUnsupported':
    'Провайдер не подписывает токены ни одним из выбранных алгоритмов.',
  'identityFailure.signingKeysUnusable':
    'В наборе ключей провайдера нет ничего, чем можно проверить токен.',
  'identityFailure.noLoginFlow':
    'Провайдер не объявляет ни device flow, ни authorization endpoint, поэтому войти через него человеку нечем.',
  'identityFailure.clientUnknown':
    'Провайдер не знает названного клиента. Сначала заведите его там.',
  'identityFailure.clusterUnreachable':
    'Проверка прошла, а кластер на новом поставщике не поднялся.',
  'identityFailure.unknown':
    'Не прошло, и кода, который консоль знает, в ответе нет. Строка ниже — собственная формулировка сервера.',

  // ---------- подтверждение смены ----------
  'switchProvider.title': 'Сменить способ входа?',
  'switchProvider.subtitle': 'Подтверждение начинает смену; результата оно не ждёт.',
  'switchProvider.target': 'Что будет применено',
  'switchProvider.wayBack.title': 'Если не заработает',
  'switchProvider.wayBack.body':
    'Кластер остаётся на том, что выполняет, пока новый провайдер не ответит, а отменить это можно на этом экране — в аккаунте, а не в консоли кластера.',
  'switchProvider.wayBack.first':
    'Пока ничего не применено, откатываться не к чему. Если провайдер не ответит, исправьте настройки здесь и примените снова.',
  'switchProvider.submit': 'Применить',
  'switchProvider.submitting': 'Начинаем',

  // ---------- применение желаемого состояния ----------
  'reconcile.section': 'Последнее применение',
  'reconcile.none': 'К этому арендатору ещё ничего не применяли.',
  'reconcile.submit': 'Применить объявленное',
  'reconcile.running': 'Применяем',
  'reconcile.done': 'Применение закончилось: {outcome}',
  'reconcile.field.actor': 'Кто попросил',
  'reconcile.field.finished': 'Закончено',
  'reconcile.col.step': 'Шаг',
  'reconcile.col.outcome': 'Исход',
  'reconcile.col.detail': 'Подробности',
  'reconcile.outcome.accepted': 'Принято',
  'reconcile.outcome.notAccepted': 'Не принято',
  'reconcile.outcome.failed': 'Сорвалось',
  'reconcile.outcome.unknown': 'Неизвестно',
  'reconcile.step.unchanged': 'Без изменений',
  'reconcile.step.applied': 'Применено',
  'reconcile.step.skipped': 'Пропущено',
  'reconcile.step.beforeContract': 'вне контракта',
  'reconcile.step.beforeContract.hint':
    'Сертификат аренды должен лежать на диске раньше, чем отрендерена конфигурация, которая на него указывает, а в контракте провижининга gdsgate шага под то, что происходит до всего его, нет.',

  // ---------- сертификат аренды ----------
  'certificate.section': 'Сертификат',
  'certificate.fromLast': 'Прочитано на последнем применении',
  'certificate.absent.noRun':
    'К этому арендатору ещё ничего не применяли, поэтому на его сертификат никто не смотрел.',
  'certificate.absent.noBlock':
    'Последнее применение о сертификате не говорит ничего: оно прошло раньше, чем появился шаг сертификата. Что лежит на диске, здесь неизвестно, и консоль это не додумывает.',

  'certificate.outcome.kept': 'Оставлен как есть',
  'certificate.outcome.kept.meaning':
    'Удостоверяющий центр не спрашивали: пара на диске годна и покрывает все имена, поэтому её переиспользовали. Это желаемый исход — центр считает ограничения на зарегистрированный домен поставщика, общий для всех арендаторов.',
  'certificate.outcome.obtained': 'Получен',
  'certificate.outcome.obtained.meaning':
    'Это применение получило пару и поставило её на место. Оба слушателя указаны на неё.',
  'certificate.outcome.waiting': 'Ждём следующей попытки',
  'certificate.outcome.waiting.meaning':
    'Попытка не удалась, и выдержка после неё ещё не кончилась, поэтому центр пока не спрашивают снова. Продление начинается за треть срока жизни до конца — это около тридцати попыток.',
  'certificate.outcome.failed': 'Не получен',
  'certificate.outcome.failed.meaning':
    'Это применение спросило и не получило. Строка выше — причина, которую назвал центр или проверка владения именем.',
  'certificate.outcome.foreign': 'Поставлен не контуром',
  'certificate.outcome.foreign.meaning':
    'Пару на диске положил не управляющий контур, поэтому она оставлена ровно как есть. Продление — дело того, кто поставил; убрать пару значит передать аренду контуру.',
  'certificate.outcome.untended': 'Здесь не выписывается',
  'certificate.outcome.untended.meaning':
    'Управляющий контур не выписывает сертификат этой аренде: либо удостоверяющий центр не настроен, либо имена в зоне, где это развёртывание не может написать запись. Пару, поставленную руками, контур подхватывает и больше не трогает.',
  'certificate.outcome.unknown': 'Исход, которого консоль не знает',
  'certificate.outcome.unknown.meaning':
    'В ответе назван исход, о котором консоль никогда не слышала. Строка выше — собственная фраза сервера.',

  'certificate.names.section': 'Набор имён',
  'certificate.names.why':
    'Оба набора, потому что только вместе они называют то, чего больше не назовёт никто: имя, под которым аренда публикуется, а сертификат его не несёт. На нашей стороне у этого нет симптомов.',
  'certificate.names.wanted': 'Публикуется под именами',
  'certificate.names.installed': 'Сертификат несёт',
  'certificate.names.installed.none':
    'Сертификата на диске нет, поэтому и имён, которые можно было бы с него прочитать, нет.',
  'certificate.names.agree': 'Наборы совпадают.',
  'certificate.names.agree.badge': 'Наборы совпадают',
  'certificate.names.diverged.title': 'Наборы разошлись',
  'certificate.names.missing':
    'Публикуется и не покрыто. Шаг сертификата на этом отказывает, и арендатор не сдаётся.',

  'certificate.trust.section': 'Что с ним сделает клиент',
  'certificate.trust.trusted': 'Публично доверенный',
  'certificate.trust.trusted.meaning': 'Центр из тех, которым клиент доверяет и так.',
  'certificate.trust.test': 'От испытательного центра',
  'certificate.trust.test.meaning':
    'Это не ошибка: испытательный центр так и отвечает. TLS настоящий — но клиент или коннектор, которому про этот корень не рассказали, шлюз не примет, так что такой сертификат клиенту не отдают.',
  'certificate.trust.notSaid': 'Не сказано',
  'certificate.trust.notSaid.meaning':
    'Пару поставил не управляющий контур, поэтому про центр, который её выписал, он ничего не утверждает.',
  'certificate.authority': 'Выписан центром',
  'certificate.authority.none':
    'Центр в отчёте не назван: этот контур здесь ничего не выписывал.',

  'certificate.valid.notAfter': 'Годен до',
  'certificate.valid.renewAt': 'Продлевается с',
  'certificate.valid.none':
    'Сертификата на диске нет, поэтому и даты, которую можно было бы с него прочитать, нет.',
  'certificate.valid.noDate': 'Этой даты в отчёте нет.',
  'certificate.valid.hint':
    'Порог продления считается из собственного срока жизни сертификата — треть срока до конца, — а не числом дней.',

  'certificate.attempts.waiting.title': 'Ждём следующей попытки',
  'certificate.attempts.waiting.body':
    'Неудач подряд: {attempts}. Следующая попытка не раньше {when}. Первая выдержка — четверть часа, дальше удвоение до суток, поэтому неудача, повторяющаяся по расписанию, — это ожидание, а не поломка.',
  'certificate.attempts.stalled.title': 'Не удалось, и следующая попытка не названа',
  'certificate.attempts.stalled.body':
    'Неудач подряд: {attempts}. Времени следующей попытки в отчёте нет, поэтому и утверждать, что она будет, здесь нечем. Строка выше — причина последней.',

  'certificate.relay.title': 'У этой аренды нет ретрансляции регистрации',
  'certificate.relay.body':
    'Аренда без сертификата ретрансляцию регистрации не публикует, поэтому коннектору на площадке клиента негде потратить join-токен. Адрес не «неизвестен», а не выдаётся: слушатель регистрации отвечает открытым текстом.',
  'certificate.relay.nearby':
    'Узла в собственной сети арендатора это не касается: он регистрируется прямо в Auth внутри неё.',

  // ---------- анонимная поверхность шлюза ----------
  'budget.section': 'Анонимная поверхность',
  'budget.absent.noRun':
    'К этому арендатору ещё ничего не применяли, поэтому бюджет ему никто не писал.',
  'budget.absent.noBlock':
    'Последнее применение о бюджете не говорит ничего: оно прошло раньше, чем управляющий контур начал писать эти ключи.',
  'budget.what':
    'На клиентском слушателе шлюза два вызова отвечают до того, как есть кого узнать. Бюджет списывается на вызов, а не на соединение.',
  'budget.rate': 'Вызовов в секунду с одного адреса',
  'budget.burst': 'Всплеск',
  'budget.burst.hint':
    'Сколько таких вызовов подряд может сделать адрес, который до этого молчал. За одним адресом сидит целый офис, поэтому всплеск щедрый, а частота нет.',
  'budget.notLimited': 'Вызов того, кого кластер уже узнал, в это число не списывается вовсе.',
  'budget.state.inForce.meaning':
    'Шлюз стартовал после того, как эти числа записали, поэтому работающий процесс их прочитал и держит к ним анонимных вызывающих.',
  'budget.state.notReadYet.meaning':
    'Шлюз держит тот же процесс, что работал в момент записи этих чисел, — значит прочитать их он не мог. `[proxy]` gdsgate читает один раз, при старте: строка выше называет, чего он держится вместо, до своего следующего старта.',
  'budget.state.notReadYet.noRestart':
    'Ничто здесь не перезапускает его, чтобы закрыть это окно: пересоздание шлюза рвёт каждую живую сессию и каждый туннель коннектора.',
  'budget.state.atTheNextStart.meaning':
    'Шлюз этой аренды не работает вовсе, поэтому никого ни к чему не держат. Эти числа прочитает ближайший старт.',
  'budget.state.notKnown.meaning':
    'Прочитал ли работающий шлюз эти числа — ответить нечем: либо контейнерный движок не удалось спросить, что работает, либо у контура нет свидетеля того, когда числа записали. Строка выше говорит, что именно из двух.',
  'budget.state.notKnown.notAFinding':
    'Это не находка: здесь не сказано, что шлюз вне бюджета, — сказано, что этот прогон не может утверждать, что он внутри. Разрешит это ближайший старт шлюза.',
  'budget.state.notWritten.meaning':
    'Шаг конфигурации на этом прогоне не дошёл, поэтому ничего не записано и ничего не прочитано. Бюджет по этому отчёту не читается.',
  'budget.state.unknown.meaning':
    'В ответе названо состояние, о котором консоль никогда не слышала. Строка выше — собственная фраза сервера.',
  'budget.aboutTheProcess':
    'Это утверждение о процессе шлюза, а не о том, что несёт его файл. Контур сравнивает, что́ работало в момент записи каждого числа, с тем, что работает сейчас.',
  'budget.refusals':
    'Как часто бюджет действительно кому-то отказывает, здесь не показано: шлюз считает отказы на слушателе телеметрии, который публикуется только внутри сети арендатора и который в управляющем контуре никто не собирает.',

  // ---------- что каждая роль прочитала из отрендеренного ----------
  'settings.state.inForce': 'Прочитано работающим процессом',
  'settings.state.notReadYet': 'Записано, и доказано не прочитано',
  'settings.state.atTheNextStart': 'Ждёт: роль не работает',
  'settings.state.notKnown': 'Ответить нечем',
  'settings.state.notWritten': 'На этом применении не записано',
  'settings.state.unknown': 'Состояние, которого консоль не знает',
  'settings.section': 'Что прочитала каждая роль',
  'settings.absent.noRun':
    'К этому арендатору ещё ничего не применяли, поэтому его ролям ничего и не рендерили.',
  'settings.absent.noBlock':
    'Это применение не говорит, что прочитали роли: оно прошло раньше, чем контур начал сравнивать. Чего держатся эти роли, из отчёта неизвестно.',
  'settings.what':
    'Роль читает свою конфигурацию один раз, при старте, поэтому у каждого отрендеренного ей значения есть зазор между «записано» и «действует». Здесь сказано, где этот зазор стоит, по роли на запись.',
  'settings.role.authority': 'Authority, управляющая плоскость аренды',
  'settings.role.proxy': 'Шлюз',
  'settings.unread': 'Ждёт ближайшего старта',
  'settings.unread.none':
    'У этой роли ближайшего старта не ждёт ничего: состояние рядом — весь ответ.',
  'settings.namesOnly':
    'Названо, а не напечатано. Значение на карточке — это значение, показанное всем, кто карточку вправе открыть, поэтому здесь не печатается ни одно. Имена — в написании самого gdsgate.',
  'settings.noRestart':
    'Ничто здесь не перезапускает роль, чтобы закрыть зазор: пересоздание рвёт каждую живую сессию и каждый туннель коннектора.',
  'settings.outside':
    'Двух вещей в этом списке нет намеренно, потому что старта они не ждут: пара сертификата, которую gdsgate смотрит заново по мере соединений, и документ политики, который перечитывает сам кластер арендатора.',

  // ---------- что сделал этот управляющий контур ----------
  'trail.section': 'Журнал',
  'trail.col.at': 'Когда',
  'trail.col.actor': 'Кто',
  'trail.col.action': 'Действие',
  'trail.col.outcome': 'Исход',
  'trail.col.detail': 'Подробности',
  'trail.count': {
    one: '{count} строка, новые первыми',
    few: '{count} строки, новые первыми',
    many: '{count} строк, новые первыми',
    other: '{count} строки, новые первыми',
  },
  'trail.capped': 'Последние 100, новые первыми',
  'trail.none.title': 'По этому арендатору ничего не записано',
  'trail.none.body': 'Сюда попадает всякое изменение, которое делает управляющий контур.',

  // ---------- каталог (консоль кластера) ----------
  'catalog.title': 'Каталог',
  'catalog.declared.title': 'Это объявленный каталог',
  'catalog.declared.body':
    'Это то, что управляющий контур сделает верным следующим применением, а не то, что у кластера есть прямо сейчас.',
  'catalog.writtenHere.body':
    'Правят его здесь, и только здесь. Изменение несёт ваше имя в аудит самого арендатора — там оно помечено как заявленное вызывающим и непроверенное.',
  'catalog.readOnly.body':
    'Править может тот, кого политика этого арендатора называет администратором, а эта сессия таким не является.',
  'catalog.declare': 'Объявить ресурс',
  'catalog.edit': 'Править',
  'catalog.remove': 'Удалить',
  'catalog.notTold.title': 'Объявлено, а кластеру не сказано',
  'catalog.notTold.written':
    '{id} есть в объявленном каталоге. До кластера достучаться не удалось, поэтому у него этого пока нет.',
  'catalog.notTold.removed':
    '{id} убран из объявленного каталога. До кластера достучаться не удалось, поэтому он всё ещё его обслуживает.',
  'catalog.notTold.next':
    'Набирать заново ничего не нужно. Это доедет следующим применением объявленного состояния — его запускает оператор из аккаунта.',
  'catalog.notTold.unnamed': 'Ваше имя то применение уже не понесёт: перед ним никто не стоит.',
  'catalog.search.label': 'Поиск по ресурсам',
  'catalog.search.placeholder': 'Имя, проект, метка',
  'catalog.filter.label': 'Фильтр по виду',
  'catalog.filter.all': 'Все',
  'catalog.col.id': 'Ресурс',
  'catalog.col.connector': 'Коннектор',
  'catalog.col.served': 'Состояние',
  'catalog.served.yes': 'Online',
  'catalog.served.unknown': '—',
  'catalog.served.unknown.hint':
    'Про этот ресурс ничего не сообщали. Коннектор объявляет обслуживаемое при открытии туннеля и берёт список из памяти, поэтому добавленный позже бэкенд — или не перезапускавшийся коннектор — прекрасно работает и не сообщает ничего. Признака живости кластер не публикует.',
  'catalog.served.no': 'Offline',
  'catalog.served.no.hint':
    'Ни один коннектор не предлагает этот идентификатор. Объявленный, но необслуживаемый ресурс — тот, о котором клиентам сказано, а дойти до него нельзя.',
  'common.yes': 'Да',
  'common.no': 'Нет',
  'catalog.col.kind': 'Вид',
  'catalog.col.address': 'Адрес',
  'catalog.col.project': 'Проект',
  'catalog.col.environment': 'Среда',
  'catalog.col.labels': 'Метки',
  'catalog.col.lease': 'Аренда',
  'catalog.lease.expired': 'истекла',
  'catalog.lease.secs': 'осталось {secs} с',
  'catalog.lease.mins': 'осталось {mins} мин',
  'catalog.lease.hours': 'осталось {hours} ч',
  'scopes.title': 'Границы',
  'scopes.count': { one: '{count} граница', few: '{count} границы', many: '{count} границ', other: '{count} границы' },
  'scopes.none.body':
    'Граница разрешает одному коннектору писать строки каталога самому, внутри заданного префикса. Нужна там, где цели появляются и исчезают быстрее, чем их успевают объявлять.',
  'scopes.new.title': 'Объявить границу',
  'scopes.edit.title': 'Граница',
  'scopes.subtitle':
    'Внутри неё названный коннектор пишет строки каталога сам. Каждая строка держит аренду и исчезает, когда её перестают продлевать.',
  'scopes.submit': 'Объявить',
  'scopes.submitting': 'Объявляем',
  'scopes.field.prefix': 'Префикс',
  'scopes.prefix.hint': 'С него начинается каждый идентификатор внутри границы: `k8s-`, `test-`.',
  'scopes.prefix.fixed': 'Не меняется у существующей границы: под ним написаны строки.',
  'scopes.prefix.placeholder': 'k8s-',
  'scopes.field.node': 'Коннектор',
  'scopes.node.unset': 'Выберите коннектор',
  'scopes.node.unreadable': 'Реестр не прочитан; введите идентификатор коннектора.',
  'scopes.field.kinds': 'Виды',
  'scopes.kinds.hint':
    'Чем цель может быть опубликована. Снимок с другим видом отвергается целиком, поэтому назовите все виды, которые находит этот коннектор.',
  'scopes.kinds.placeholder': 'начните вводить',
  'scopes.kinds.remove': 'Убрать вид',
  'scopes.node.hint': 'Идентификатор коннектора, которому разрешено публиковать здесь. Узлы — на экране узлов.',
  'scopes.field.kind': 'Вид',
  'scopes.kind.hint': 'Чем цель может быть опубликована. Снимок с другим видом отвергается целиком.',
  'scopes.field.project': 'Проект',
  'scopes.project.hint': 'Пишется на каждой строке здесь.',
  'scopes.field.environment': 'Среда',
  'scopes.environment.placeholder': 'prod, staging, test',
  'scopes.check.prefixRequired': 'Границе нужен префикс: с него начинается каждый идентификатор внутри неё.',
  'scopes.check.prefixShape': 'Буквы, цифры, `-`, `_` или `.` — то, что может нести идентификатор ресурса.',
  'scopes.check.nodeRequired': 'Выберите коннектор, который наполняет границу.',
  'scopes.check.kindsRequired': 'Назовите хотя бы один вид, иначе каждый снимок будет отвергнут.',
  'scopes.check.environmentRequired': 'Граница называет среду для каждой строки, которую держит.',
  'scopes.check.noWhitespace': 'Без пробелов.',
  'scopes.check.maxLiveRange': 'Целое число строк, не меньше одной.',
  'scopes.check.leaseRange': 'Целое число секунд, не меньше одной.',
  'scopes.environment.hint': 'Пишется на каждой строке здесь и никогда не берётся у источника.',
  'scopes.field.maxLive': 'Потолок строк',
  'scopes.maxLive.hint':
    'Сколько строк граница держит одновременно, считая строки прошлых снимков с неистёкшей арендой.',
  'scopes.field.leaseSecs': 'Аренда, секунд',
  'scopes.leaseSecs.hint': 'Сколько строка живёт после снимка, который её принёс.',
  'scopes.field.fallback': 'Цель, которую коннектор ещё не опубликовал',
  'scopes.fallback.hint':
    'Зонд публикует найденное каждый проход, поэтому у цели, созданной между проходами, строки каталога ещё нет. Здесь сказано, доступна ли она тем временем.',
  'scopes.fallback.parent': 'Доступна через родительский ресурс, как было до границ',
  'scopes.fallback.deny': 'Недоступна, пока следующий проход её не опубликует',
  'scopes.field.hostKey': 'Ключ хоста найденной ssh-цели',
  'scopes.hostKey.hint':
    'Клиент сверяет хост, на который попал, с этим отпечатком — от него зависит, от чего защищена сессия.',
  'scopes.hostKey.annotation':
    'Берётся у самого объекта (gdsgate.io/ssh-host-key); цель без него не обслуживается',
  'scopes.hostKey.tofu':
    'Первый увиденный ключ считается верным на время жизни строки, смена после этого отвергается',
  'scopes.blurb':
    'Внутри границы названный коннектор пишет строки каталога сам. Каждая строка держит аренду и исчезает, когда её перестают продлевать.',
  'scopes.none': 'Ни одной границы не объявлено.',
  'scopes.add': 'Объявить границу',
  'scopes.col.prefix': 'Префикс',
  'scopes.col.node': 'Коннектор',
  'scopes.col.kinds': 'Виды',
  'scopes.col.environment': 'Среда',
  'scopes.col.rows': 'Строки',
  'scopes.col.lease': 'Аренда',
  'scopes.leaseSecs': '{secs} с',
  'scopes.disabled': 'выключена',
  'scopes.remove': 'Снять',
  'scopes.removed': 'Граница снята вместе со строками, которые она держала.',
  'scopes.declared': 'Граница объявлена.',
  'scopes.form.prefix': 'Префикс',
  'scopes.form.node': 'Идентификатор коннектора',
  'scopes.form.kinds': 'Виды через запятую',
  'scopes.form.project': 'Проект',
  'scopes.form.environment': 'Среда',
  'scopes.form.maxLive': 'Потолок строк',
  'scopes.form.leaseSecs': 'Аренда, секунд',
  'scopes.form.submit': 'Объявить',
  'scopes.form.cancel': 'Отмена',
  'catalog.col.approvers': 'Согласующих',
  'catalog.under': 'внутри {parent}',
  'catalog.approvers.inherited': 'по умолчанию',
  'catalog.empty.title': 'Ничего не подходит',
  'catalog.empty.body': 'Измените фильтр или строку поиска.',
  'catalog.none.title': 'Каталог пуст',
  'catalog.none.body': 'Пока для арендатора не объявлен ресурс, не обслуживается ничего.',
  'catalog.none.declare':
    'Пока не объявлен ресурс, не обслуживается ничего. Объявить первый — то, ради чего эта консоль и нужна.',
  'catalog.count': {
    one: '{count} ресурс',
    few: '{count} ресурса',
    many: '{count} ресурсов',
    other: '{count} ресурса',
  },
  'catalog.noLabels': '—',

  // ---------- один ресурс ----------
  'resource.new.title': 'Объявить ресурс',
  'resource.edit.title': 'Переписать ресурс',
  'resource.subtitle':
    'Сначала объявляется, потом доносится до кластера. Отправляются все поля: запись переписывает ресурс целиком, а не сливается с ним.',
  'resource.submit': 'Объявить',
  'resource.submitting': 'Объявляем',
  'resource.field.id': 'Имя',
  'resource.field.kind': 'Вид',
  'resource.field.project': 'Проект',
  'resource.field.environment': 'Среда',
  'resource.field.hostname': 'Имя хоста',
  'resource.field.port': 'Порт',
  'resource.field.aliases': 'Псевдонимы',
  'resource.field.parent': 'Родитель',
  'resource.field.approvers': 'Согласующих',
  'resource.field.labels': 'Метки',
  'resource.id.hint':
    'То, что просят клиенты и что называет политика. Это адрес, по которому идёт запись, и потом он не меняется.',
  'resource.id.fixed':
    'Зафиксировано. Другое имя — другой ресурс: одно объявить, другое удалить, а удаление закрывает доступ.',
  'resource.id.placeholder': 'orders-db',
  'resource.kind.hint': 'Что за ним стоит. От этого зависит, на каком языке с ним говорит шлюз.',
  'resource.project.hint':
    'Группировка, по которой пишут политику. Пустым не бывает; собственное значение сервера по умолчанию — default.',
  'resource.environment.hint':
    'Вторая группировка, по которой обычно пишут политику. Значение сервера по умолчанию — dev.',
  'resource.hostname.hint':
    'Имя, по которому ресурс достают клиенты, — `gdsgate up` публикует его в зоне `.gds`. Это не адрес бэкенда: тот задаётся в коннекторе и не публикуется. Пусто — берётся идентификатор выше.',
  'resource.port.hint':
    'Порт, который клиент слушает у себя, а не порт бэкенда. Пусто — берётся общеизвестный порт этого вида.',
  'resource.port.required':
    'Обязателен для этого вида: у TCP, MCP, модельной службы и выхода наружу общеизвестного порта нет. Выход наружу не набирают по имени — его пишут в $HTTP_PROXY вместе с портом.',
  'resource.aliases.hint': 'Другие имена, которыми могут пользоваться клиенты, через пробел.',
  'resource.parent.hint': 'Для базы на узле: ресурс, на котором она стоит.',
  'resource.approvers.hint':
    'Сколько одобрений нужно заявке на этот ресурс. Пусто — столько, сколько требует политика в целом.',
  'resource.labels.hint':
    'По одной паре ключ=значение в строке. Без запятых и без двух пробелов подряд: текстовая форма каталога их не экранирует.',
  'resource.field.nativeName': 'Собственный домен',
  'resource.nativeName.hint':
    'Домен, на котором приложение само считает, что отвечает, — так, как его пишет url бэкенда в коннекторе. Это не имя хоста выше: то — одна метка, которую выдаёт этот кластер, а это целое имя, которым владеет кто-то другой. Объявление ничего не публикует: это факт, который нужен клиенту, прежде чем он сможет отвечать на такое имя у себя. Пусто — у приложения нет собственного имени.',
  'resource.nativeName.placeholder': 'grafana.example.com',
  'resource.field.pin': 'Описания инструментов',
  'resource.pin.label': 'Отклонять инструмент с изменившимся описанием',
  'resource.pin.hint':
    'Первое увиденное описание инструмента становится образцом, и вызов инструмента, описание которого с тех пор изменилось, отклоняется, пока изменение не одобрят. Описания отдаёт только сервер MCP, поэтому флажок показан лишь для этого вида.',
  'resource.whole.title': 'Ресурс записывается целиком',
  'resource.whole.body':
    'Запись каждый раз переписывает метки, адресацию, родителя и порог. Оставленное здесь пустым не остаётся прежним, а исчезает с ресурса.',
  'resource.declared.body':
    'Сначала пишется объявление, потом об этом узнаёт кластер. Если до кластера не достучались, объявление остаётся в силе и доедет следующим применением.',
  'resource.notNameable.title': 'Ваш провайдер называет вас в форме, которую кластер не запишет',
  'resource.notNameable.body':
    'Не изменено ничего — ни здесь, ни в кластере. gdsgate записывает человека за административным вызовом и принимает имя только из букв, цифр, пробелов и . _ - @ + ’.',
  'resource.notNameable.fix':
    'Чинится на стороне вашего поставщика удостоверений — выдачей sub в такой форме. Повтор до этого ничего не изменит.',
  'resource.forbidden.title': 'Этот кластер не пускает вас к каталогу',
  'resource.forbidden.body':
    'Политика больше не называет вас администратором этого кластера. Роль пересчитывается на каждом вызове, поэтому она может измениться при открытой сессии.',
  'resource.remove.title': 'Удалить ресурс?',
  'resource.remove.subtitle': '{id} уходит из объявленного каталога.',
  'resource.remove.submit': 'Удалить',
  'resource.remove.submitting': 'Удаляем',
  'resource.remove.effect.title': 'Что делает удаление',
  'resource.remove.effect.body':
    'Оно закрывает доступ. Запрос к ресурсу, которого нет в каталоге кластера, отклоняется до того, как считается политика, — сразу для всех.',
  'resource.remove.effect.until': 'Пока удаление не дошло до кластера, кластер его обслуживает.',
  'resource.gone.title': 'В каталоге его нет',
  'resource.gone.body':
    'Для этого арендатора ничего с таким именем не объявлено — возможно, его уже удалили. Список перечитан.',
  'resource.check.idRequired': 'Введите имя.',
  'resource.check.idWhitespace': 'В имени ресурса не бывает пробельных символов.',
  'resource.check.projectRequired': 'Введите проект.',
  'resource.check.environmentRequired': 'Введите среду.',
  'resource.check.noWhitespace': 'Здесь есть пробельные символы.',
  'resource.check.portRequired': 'У этого вида нет общеизвестного порта, поэтому порт обязателен.',
  'resource.check.portRange': 'Порт — целое число от 1 до 65535.',
  'resource.check.approversNumber': 'Целое число или пусто.',
  'resource.check.labelShape': 'По одной паре ключ=значение в строке, и обе части непустые.',
  'resource.check.labelComma': 'В метке не бывает запятой: текстовая форма каталога её не экранирует.',
  'resource.check.labelSpaces':
    'В метке не бывает двух пробелов подряд: в текстовой форме каталога ими разделяются поля.',
  'resource.check.nativeName':
    'Целый домен, не меньше двух меток, — app.example.com. Одна метка — это имя в собственной зоне кластера, то есть имя хоста выше.',

  // ---------- виды ресурсов ----------
  'resourceKind.ssh': 'SSH',
  'resourceKind.postgres': 'PostgreSQL',
  'resourceKind.mysql': 'MySQL',
  'resourceKind.kubernetes': 'Kubernetes',
  'resourceKind.tcp': 'TCP',
  'resourceKind.mcp': 'MCP',
  'resourceKind.llm': 'Модельная служба',
  'resourceKind.web': 'Web App',
  'resourceKind.webEgress': 'Egress',

  // ---------- атрибуция ----------
  'attribution.title': 'Это записывается под вашим именем',
  'attribution.body': 'Изменение попадёт в журнал управляющего контура как {actor}.',
  'attribution.tenantAudit':
    'Там, где изменение доходит до кластера, имя едет вместе с ним — с пометкой, что оно заявлено вызывающим и не проверено: арендатор не может подтвердить, кто именно у поставщика нажал кнопку.',
  'attribution.notNameable':
    'Имя, которого кластер не запишет, отвергается на границе, и не изменяется ничего ни на одной стороне.',

  // ---------- отказы ----------
  'error.network.title': 'Сервер не ответил',
  'error.network.body': 'Запрос не дошёл. Консоль ничего не изменила.',
  'error.unexpected.title': 'Ответ, которого консоль не понимает',
  'error.unexpected.body':
    'Сервер отказал чем-то, для чего у консоли нет формулировки. Его собственная — ниже.',
  'error.unauthenticated.title': 'Вход не выполнен',
  'error.unauthenticated.body': 'Сессии нет или она кончилась. Достаточно войти снова.',
  'error.badCredentials.title': 'Эти учётные данные не подходят друг к другу',
  'error.badCredentials.body':
    'Что именно неверно, сознательно не сказано: любая разница — способ узнать, какие адреса заведены.',
  'error.tooManyAttempts.title': 'Слишком много попыток',
  'error.tooManyAttempts.body':
    'Окно неудачных попыток переполнено. Оно опустеет само, и верный пароль в нём тоже не проходит.',
  'error.tooManyAttempts.wait': 'Повторите через {wait}.',
  'error.forbidden.title': 'Не разрешено',
  'error.forbidden.body':
    'Эта роль такого не несёт вовсе. О том, какие арендаторы существуют, ответ ничего не сообщает.',
  'error.signInUnavailable.title': 'Сейчас эта консоль никого не пускает',
  'error.signInUnavailable.body':
    'Либо к кластеру не применён поставщик удостоверений, либо идёт его смена, либо выбран поставщик поставщика, которого это развёртывание не держит. Выход из всех трёх — аккаунт.',
  'error.signInExpired.title': 'Этот вход завершить нельзя',
  'error.signInExpired.body':
    'Его начали в другом браузере, или слишком давно, или он уже завершён. Ничего не потеряно и ничего не сломано: начните вход заново с этого экрана.',
  'error.signInRefused.title': 'Удостоверение не принято',
  'error.signInRefused.body':
    'То, что вернул провайдер, не прошло проверку. Какая именно проверка не прошла, сознательно не сказано; подробности — в логе управляющего контура.',
  'error.providerUnreachable.title': 'Поставщик удостоверений не ответил',
  'error.providerUnreachable.body':
    'Он не ответил или отказал по своим причинам. Это не про вас и не про то, что вы набрали: повторите позже.',
  'error.notNameable.title': 'Ваше имя кластер не запишет',
  'error.notNameable.body':
    'Не изменено ничего — ни здесь, ни в кластере. gdsgate принимает имя оператора из букв, цифр, пробелов и . _ - @ + ’; sub другой формы записать в его аудит нельзя.',
  'error.noSuchConsole.title': 'На этом домене консоли нет',
  'error.noSuchConsole.body':
    'Домен не принадлежит ни одному нашему арендатору, либо эта часть консоли на нём не отвечает.',
  'error.tenantNotFound.title': 'Такого арендатора нет',
  'error.tenantNotFound.body':
    'Арендатора с таким именем нет, либо учётная запись не имеет на него прав. Ответ одинаков в обоих случаях.',
  'error.resourceNotFound.title': 'Такого ресурса нет',
  'error.resourceNotFound.body': 'Его нет в объявленном каталоге этого арендатора.',
  'error.accountNotFound.title': 'Такой учётной записи нет',
  'error.accountNotFound.body': 'По этому адресу учётной записи не заведено.',
  'error.invalidRequest.title': 'Сервер это не принял',
  'error.invalidRequest.body': 'Что-то в отправленном непригодно. Поле названо.',
  'error.providerUnavailable.title': 'Своего поставщика удостоверений здесь нет',
  'error.providerUnavailable.body':
    'В этом развёртывании нет своего поставщика удостоверений, поэтому указать на него кластер нельзя.',
  'error.identityProviderRefused.title': 'Этот провайдер удостоверений кластер не понесёт',
  'error.identityProviderRefused.body':
    'Провайдера спросили до того, как что-либо создано, и по его ответу выходит, что управляющая плоскость кластера на нём не поднимется — либо войти через него будет некому. Ничего не создано.',
  'error.conflict.title': 'Состояние этого не позволяет',
  'error.conflict.body': 'Что-то занято либо текущее состояние не допускает операции.',
  'error.changeUnderWay.title': 'Смена уже идёт',
  'error.changeUnderWay.body':
    'Для этого арендатора уже идёт смена поставщика удостоверений. Вторая отклоняется, а не ставится в очередь.',
  'error.noFailureToDismiss.title': 'Убирать нечего',
  'error.noFailureToDismiss.body': 'Последняя смена не падала, значит и отчёта нет.',
  'error.tenantUnreachable.title': 'Кластер арендатора не ответил',
  'error.tenantUnreachable.body':
    'Управляющий контур не смог до него достучаться либо получил отказ. О том, работает ли кластер для своих пользователей, это не говорит ничего.',
  'nav.cluster.nodes': 'Узлы',
  'nav.cluster.audit': 'Аудит',
  'nodes.title': 'Узлы',
  'nodes.subtitle': 'Что этот кластер завёл — прочитано у самого кластера.',
  'nodes.none': 'Пока никто не присоединился',
  'nodes.none.body':
    'Коннектор присоединяется по одноразовому токену. Выпустите его выше и потратьте на машине, которая будет обслуживать ваши ресурсы.',
  'nodes.filter.label': 'Показывать',
  'nodes.filter.all': 'Все',
  'nodes.filter.active': 'Активные',
  'nodes.filter.revoked': 'Отозванные',
  'nodes.noMatch': 'В этой вкладке пусто',
  'nodes.noMatch.body':
    'Узлы в реестре есть, но ни один не в этом состоянии. Узел активен, пока открыто продление, и отозван после — других состояний у строки реестра нет.',
  'nodes.col.name': 'Имя',
  'nodes.col.role': 'Роль',
  'nodes.col.id': 'Идентификатор в реестре',
  'nodes.col.enrolled': 'Заведён',
  'nodes.col.state': 'Состояние',
  'nodes.name.claimed.why':
    'Это имя узел сообщил о себе при вступлении. Токен выпускается на роль, а не на машину, поэтому в имени кластеру нечего проверять.',
  'nodes.name.none': 'себя не назвал',
  'nodes.state.active': 'Активен',
  'nodes.state.revoked': 'Отозван',
  'nodes.mint': 'Выпустить join-токен',
  'nodes.mint.working': 'Выпускаем',
  'nodes.revoke': 'Отозвать',
  'nodes.revoked': 'Узел {id} больше не продлится',
  'nodes.forget': 'Забыть',
  'nodes.forget.working': 'Убираем',
  'nodes.forgotten': 'Узел {id} убран из реестра; отзыв остаётся в силе',
  'nodes.token.title': 'Это единственная копия',
  'nodes.token.body':
    'Потратить можно один раз и в течение {ttl} минут; здесь он нигде не сохраняется. Потеряли — выпустите новый: это дешевле, чем консоль, которая хранит учётные данные.',
  'nodes.token.label': 'Join-токен',
  'nodes.token.how':
    'На машине, которая будет обслуживать ваши ресурсы: подайте его в `gdsgate join` на стандартный вход — не аргументом, аргументы читает каждый процесс на том хосте.',
  'nodes.install': 'Поставить коннектор',
  'nodes.install.working': 'Готовим',
  'nodes.install.client': 'Поставить клиент',
  'nodes.install.title': 'Выполните это на машине',
  'nodes.install.body.connector':
    'Одна команда, действует {ttl} минут. Она скачивает бинарь, на котором работает эта аренда, проверяет его и регистрирует коннектор — спрашивая только имя и что обслуживать, оба уже подставлены.',
  'nodes.install.body.client':
    'Одна команда, действует {ttl} минут. Она ставит клиент на ту машину; вход — потом, от имени того, кто его запускает.',
  'nodes.install.label': 'Команда',
  'nodes.install.how':
    'Root нужен только чтобы записать `/usr/local/bin/gdsgate`. Регистрация идёт от вашего пользователя, поэтому несколько коннекторов под разными учётными записями на одном хосте — обычное дело.',
  'offered.title': 'Предложено коннекторами',
  'offered.subtitle':
    'Что ваши коннекторы объявляют обслуживаемым. Объявите ресурс — и кластер начнёт к нему маршрутизировать.',
  'offered.none': 'Никто ничего не предлагает',
  'offered.allDeclared': 'Всё предложенное объявлено',
  'offered.allDeclared.body':
    'У каждого ресурса, который называют ваши коннекторы, есть строка в каталоге. Из этой секции ресурс уходит, как только объявлен: объявленному место на экране каталога.',
  'offered.none.body':
    'Коннектор перечисляет обслуживаемое, когда подключается. Пусто — значит ни один коннектор не запущен либо ни один не называет бэкенда.',
  'offered.unusable': 'Объявить нельзя',
  'offered.unusable.why':
    'Идентификатор в каталоге — строчные буквы, цифры и дефисы, не более 63 символов. Коннектор предлагает не такой, и строку под него не написать.',
  'offered.unusable.fix':
    'Исправьте на коннекторе: `--serve <id>=<kind>` — и предложение появится здесь снова',
  'offered.col.resource': 'Ресурс',
  'offered.col.kind': 'Вид',
  'offered.col.node': 'Предлагает',
  'offered.col.offered': 'Заявлено',
  'offered.claimed.why':
    'Что обслуживает коннектор — это его собственное заявление при подключении. Кластер начинает маршрутизировать, когда ресурс объявлен, а не потому что коннектор его назвал.',
  'offered.declare': 'Объявить',
  'offered.undeclared.title': 'Предложено — не значит доступно',
  'offered.undeclared.body':
    'Пока ресурс не объявлен, кластер к нему ничего не маршрутизирует: коннектор может быть на связи, а ресурс — недоступен. Объявление пишется в ваш каталог, и в строке аудита стоит ваше имя.',
  'offered.restart.title': 'Список меняется при перезапуске коннектора',
  'offered.restart.body':
    'Коннектор объявляет обслуживаемое по той конфигурации, с которой запустился, и не перечитывает файл на ходу. Только что добавленный бэкенд появится здесь после перезапуска этого коннектора.',
  'audit.title': 'Аудит',
  'audit.subtitle': 'Кто и что делал в этом кластере, с чем и чем это кончилось.',
  'audit.read.title': 'Открытие этого экрана попадает в журнал',
  'audit.read.body':
    'Кластер пишет строку на каждое чтение журнала аудита, включая это: кто читал и по чему фильтровал. Так задумано — способ прочитать журнал, не оставив следа, был бы единственным, что разбор инцидента не восстановит.',
  'audit.proof.title': 'Это то, что говорит кластер, а не доказательство',
  'audit.proof.body':
    'Журнал — хэш-цепочка, и переписанная цепочка ответила бы этому экрану так же гладко. Проверяет её `gdsgate verify-audit`, запущенный против базы самого кластера.',
  'audit.col.when': 'Когда',
  'audit.col.actor': 'Кто',
  'audit.actor.agent': 'агент',
  'audit.actor.agent.of': 'агент {owner}',
  'audit.actor.agent.why':
    'Программа, действующая по чьей-то делегации: его собственный доступ, суженный до одолженного. Всё, что она сделала здесь, мог сделать и он сам.',
  'audit.actor.workload': 'нагрузка',
  'audit.actor.workload.why':
    'Машинная личность, не человек. Она предъявила сертификат, выпущенный этим кластером, а не вошла по логину.',
  'audit.claimed.label': 'Заявленный оператор',
  'audit.claimed.why':
    'Кем назвал действующего контур, сделавший вызов. Заявлено и не проверено: кластер удостоверил вызывающего, а не это имя.',
  'audit.col.class': 'Класс',
  'audit.event.label': 'Записано как',
  'audit.event.why':
    'Имя, под которым строка легла в цепочку. Все решения политики кластер пишет под одним именем, а рядом кладёт действие, по которому решал, — оно и стоит в заголовке этой панели.',
  'audit.code.label': 'Код',
  'audit.code.why':
    'Что это за событие, числом. Код не переиспользуется и не переименовывается, поэтому в строках, записанных с разницей в годы, он значит одно и то же — в отличие от слов рядом, которые уже однажды переименовали. В строках старше кодов его нет.',
  'audit.session.denied': 'Отказано: {reason}',
  'audit.session.label': 'Сессия',
  'audit.session.timeline': 'Эта сессия',
  'audit.session.window':
    'Из прочитанной страницы. У сессии, начавшейся раньше, видно только попавшую в неё часть.',
  'audit.col.action': 'Что сделал',
  'audit.col.target': 'С чем',
  'audit.col.outcome': 'Итог',
  'audit.col.detail': 'Подробности',
  'audit.claimed': 'названо контуром, не проверено',
  'audit.truncated': 'обрезано',
  'audit.filter.label': 'Класс',
  'audit.filter.all': 'Все классы',
  'audit.filter.admin-action': 'Администрирование',
  'audit.filter.authorization': 'Решения',
  'audit.filter.authentication': 'Входы',
  'audit.filter.session-activity': 'Сессии',
  'audit.filter.resource-integrity': 'Целостность ресурсов',
  'audit.filter.tooNew':
    'Этот класс появился в gdsgate {version}, и кластер отверг само слово: аренда работает на сборке до неё. Не прочитано ничего, остальные классы читаются, а эта вкладка ответит, когда аренду переведут на {version} или новее.',
  'audit.filter.tooNew.maybe':
    'Этот класс появился в gdsgate {version}. Кластер, работающий на версии ниже, отказывает чтению целиком, а не отвечает пустотой, — поэтому если не читается только эта вкладка, а остальные читаются, дело в версии, а не в политике.',
  'audit.actor.label': 'Кто',
  'audit.actor.placeholder': 'Кто угодно',
  'audit.actor.apply': 'Отфильтровать',
  'audit.actor.clear': 'Сбросить',
  'audit.actor.why':
    'Фильтр по человеку отвечает на вопрос «что он делал» — по всем ресурсам и за все часы. Ради этого журнал аудита и ведут, и по этой же причине право читать его — не то же самое, что право администрировать кластер.',
  'audit.none': 'В журнале пока пусто',
  'audit.none.body':
    'Первые строки появляются, как только кто-то входит или узел присоединяется. Пусто — значит не случилось ни того, ни другого.',
  'audit.noMatch': 'Ничего не подошло',
  'audit.noMatch.body':
    'Ни одна строка прочитанной страницы не подходит под фильтр. Расширьте его или запросите больше строк.',
  'nodes.revoke.means.title': 'Что отзыв делает, а что нет',
  'nodes.revoke.means.body':
    'Он закрывает продление: свежий сертификат узел не получит. Тот, что у него уже есть, остаётся проверяемым до истечения, поэтому открытую прямо сейчас сессию это не рвёт.',
  'error.clusterRefused.title': 'Кластер отказал',
  'error.clusterRefused.body':
    'Вызов дошёл до кластера, и он ответил отказом. Что именно он сказал — ниже, его словами: политика, которая этого не разрешает, невыполняющаяся роль или узел, которого он не знает.',
  'error.auditClassUnknown.title': 'Кластер не знает такого класса',
  'error.auditClassUnknown.body':
    'Его gdsgate отверг это слово там, где разбирает аргументы, поэтому не прочитано вообще ничего — это не отказ политики и не журнал без таких строк. Остальные классы читаются.',
  'error.internal.title': 'Отказало хранилище управляющего контура',
  'error.internal.body':
    'Подробности ушли в его лог, а не на эту страницу: сообщение хранилища может нести запрос, а запрос — данные.',

  'toast.tenantDeclared': '{name} заведён',
  'toast.changeStarted': 'Смена начата',
  'toast.resourceDeclared': '{id} объявлен, и кластеру сказано',
  'toast.resourceRemoved': '{id} удалён, и кластеру сказано',

  'route.notFound.title': 'Такой страницы нет',
  'route.notFound.body': 'Адрес не соответствует ничему в этой консоли.',

  // ---------- заглушки ----------
  'stub.banner.title': 'Демонстрационные данные',
  'stub.banner.body':
    'Сервера здесь нет. Всё на экране порождено в браузере, и до кластера ничего не доходит.',
  'stub.entry.account':
    'Эта страница — аккаунт, который в развёрнутом виде обслуживается с домена поставщика.',
  'stub.entry.cluster':
    'Эта страница — консоль кластера {domain}, которая в развёрнутом виде обслуживается с этого домена.',
  'stub.entry.toCluster': 'Открыть вместо неё консоль кластера',
  'stub.entry.toAccount': 'Открыть вместо неё аккаунт',
  'stub.accounts.title': 'Учётные записи для показа',
  'stub.accounts.password': 'Пароль у всех трёх — {password}.',
  'stub.accounts.admin': '{email} — оператор поставщика.',
  'stub.accounts.support': '{email} — поддержка: всё читает, ничего не меняет.',
  'stub.accounts.tenancy': '{email} — представитель арендатора.',
  'stub.accounts.attempts':
    'Пять неудач по одному адресу переполняют окно, и вместе с ним перестаёт работать верный пароль — так же, как в настоящем.',
  'stub.identity.title': 'Что здесь можно увидеть',
  'stub.identity.timing': 'Применение занимает около шести секунд и завершается по стенным часам.',
  'stub.identity.rejected':
    'Издатель, имя хоста которого начинается с broken., отклоняется до того, как что-либо тронуто.',
  'stub.identity.failed':
    'Издатель, имя хоста которого начинается с unstable., проходит проверку и потом не поднимается.',
  'stub.identity.provider':
    'В развёртывании может не быть своего поставщика удостоверений — тогда этот вариант недоступен вовсе.',
  'stub.identity.provider.off': 'Считать, что своего поставщика в развёртывании нет',
  'stub.identity.provider.on': 'Считать, что он есть',
  'stub.cluster.title': 'Поставщик удостоверений в этом показе',
  'stub.cluster.body':
    'Настоящего провайдера здесь нет, поэтому показ играет его сам: переход уводит к поддельному провайдеру, который возвращает на этот же адрес с кодом и state — так же, как настоящий.',
  'stub.cluster.expired':
    'Открыть /login?code=x&state=y руками — это возврат, которого никто не начинал: ровно то, что получает истраченный или чужой код.',
  'stub.cluster.vouches': 'Что отвечает поддельный провайдер:',
  'stub.cluster.as.admin': 'Поручиться за человека из группы администраторов',
  'stub.cluster.as.stranger': 'Поручиться за человека не из группы этого кластера',
  'stub.cluster.as.refused': 'Вернуть удостоверение, которое не проходит проверку',
  'stub.cluster.as.unreachable': 'Не ответить вовсе',
  'stub.catalog.title': 'Что здесь можно увидеть',
  'stub.catalog.body':
    'Запись сначала объявляется, потом доносится до кластера, и сорвать можно любую из двух половин.',
  'stub.catalog.reachable.off': 'Считать, что до кластера не достучаться',
  'stub.catalog.reachable.on': 'Считать, что кластер снова доступен',
  'stub.catalog.nameable.off': 'Считать, что провайдер называет вас f:71c1e:ivan',
  'stub.catalog.nameable.on': 'Считать, что он снова называет вас по-человечески',
  'stub.usage.title': 'Что здесь можно увидеть',
  'stub.usage.body':
    'Сессии и люди считаются из выгрузки аудит-цепочки арендатора, до которой управляющий контур здесь не дотягивается, — поэтому по умолчанию они приходят отсутствующими, с причиной. Если включить, они считаются, и среди посчитанных есть настоящий ноль.',
  'stub.usage.other':
    'У любого арендатора, кроме Acme, кластера здесь за спиной нет, поэтому все четыре цифры приходят отсутствующими.',
  'stub.usage.export.off': 'Считать, что выгрузку аудита прочитать нельзя',
  'stub.usage.export.on': 'Считать, что выгрузку аудита прочитать можно',
  'stub.rollout.title': 'Что здесь можно увидеть',
  'stub.rollout.body':
    'Версии на карточках — это показания, а не теги: у одного арендатора шлюз и управляющая плоскость разведены названным разрывом, у другого показание шлюза отброшено. Коннектор и клиент не читаются нигде.',
  'stub.rollout.waves':
    'Acme — канареечная волна, остальные — «Остальные». Один арендатор из второй волны не сдвигается намеренно: так видно и волну, которую подписать нельзя, и волну, которую можно.',
  'stub.rollout.soak':
    'Волна обязана простоять на новой версии, прежде чем её можно подписать. Здесь это переключатель, а не полчаса.',
  'stub.rollout.soak.off': 'Считать, что волна ещё не простояла положенное',
  'stub.rollout.soak.on': 'Считать, что волна простояла положенное',
  'stub.rollout.repaired':
    'Арендатор, который не сдвигается, будет проваливаться, пока кто-нибудь его не починит. Этот поход — второй переключатель: с ним повтор сдвига проходит.',
  'stub.rollout.repaired.off': 'Считать, что провальный арендатор всё ещё сломан',
  'stub.rollout.repaired.on': 'Считать, что провальный арендатор починен',
};
